import { useFormik } from 'formik';
import { useState } from 'react';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { SearchAdminEmailInput } from '@/bundle/_MasterAdmin/ui/SearchAdminEmailInput/SearchAdminEmailInput';
import { Button } from '@/components/Button/Button';
import { CREATE_ADMIN_INITIAL_VALUES, CREATE_ADMIN_VALIDATION_SCHEMA } from '@/bundle/_MasterAdmin/ui/const';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormikPanelRadioGroup } from '@/components/form/fields/FormikPanelRadioGroup/FormikPanelRadioGroup';
import { SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS } from '@/const/formConfigs';
import { useAssignUser } from '@/bundle/shared/hooks/useAssignUser';
import { loadUserInfo } from '@/bundle/shared/api';
import {
  addInternalOrganizationUserRole,
  createInternalOrganizationUser,
  getInternalOrganizationUserAddingStatus,
} from '../../api/api';
import { TRIAL_MODE_SUBSCRIPTION_REQUIRED_ERROR_MESSAGE, HTTP_BAD_REQUEST_STATUS } from '@/api/const';
import { getExcludedResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { CreateInternalOrganizationUserPayloadType } from '@/api/v1/organization/internal/createInternalOrganizationUserApi';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { useNavigate } from 'react-router';
import { useSubscriptionRequiredError } from '@/bundle/shared/hooks/useSubscriptionRequiredError';
import { getAcceptMasterServiceAgreementPath } from '@/bundle/Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/path';
import { SubscriptionRequiredModal } from '@/bundle/shared/components/modals/SubscriptionRequiredModal';

type CreateAdminFormType = {
  onSuccess: () => void;
  onCancel: () => void;
};

type CreateAdminFormValuesType = CreateInternalOrganizationUserPayloadType;

export const CreateAdminForm = ({ onSuccess, onCancel }: CreateAdminFormType) => {
  const navigate = useNavigate();
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);
  const { hasSubscriptionRequiredError, resetSubscriptionRequiredError, handleSubscriptionRequiredError } =
    useSubscriptionRequiredError();

  const validationSchema = assignedUserId ? null : CREATE_ADMIN_VALIDATION_SCHEMA;

  const { data: addingUserStatusData } = useQuery({
    queryKey: ['get_internal_organization_user_adding_status', assignedUserId],
    queryFn: () => getInternalOrganizationUserAddingStatus(assignedUserId),
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_admin_details', assignedUserId],
    queryFn: () => loadUserInfo(assignedUserId),
    enabled: !!assignedUserId && !!addingUserStatusData?.body?.is_available_to_add,
  });

  const {
    data: createInternalAdminData,
    mutate: createInternalAdminMutate,
    isPending: isPendingCreateInternalAdmin,
    reset: createInternalAdminReset,
  } = useMutation({
    mutationKey: ['create_internal_admin'],
    mutationFn: (values: CreateAdminFormValuesType) => {
      return createInternalOrganizationUser(values);
    },
    onSuccess(response) {
      if (response?.error) {
        return handleSubscriptionRequiredError(response?.error);
      }

      onSuccess();
    },
  });

  const {
    data: addInternalAdminRoleData,
    mutate: addInternalAdminRoleMutate,
    isPending: isPendingAddInternalAdminRole,
    reset: addInternalAdminRoleReset,
  } = useMutation({
    mutationKey: ['add_internal_organization_user_role'],
    mutationFn: (values: CreateAdminFormValuesType) => {
      const payload = {
        user_id: loadUserData?.body?.id,
        role: values.role,
      };

      return addInternalOrganizationUserRole(payload);
    },
    onSuccess(response) {
      if (response?.error) {
        return handleSubscriptionRequiredError(response?.error);
      }

      onSuccess();
    },
  });

  const formik = useFormik<CreateAdminFormValuesType>({
    initialValues: CREATE_ADMIN_INITIAL_VALUES,
    validationSchema,
    onSubmit: (values) => {
      if (assignedUserId) {
        return addInternalAdminRoleMutate(values);
      }

      return createInternalAdminMutate(values);
    },
  });

  const searchUserEmail = (userId: string) => {
    formik.setErrors({});
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.email && formik.errors?.email) {
      formik.setTouched({ email: true });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    createInternalAdminReset();
    addInternalAdminRoleReset();

    formik.resetForm({
      values: {
        ...CREATE_ADMIN_INITIAL_VALUES,
        role: formik.values.role,
      },
    });
    formik.setFieldValue('email', formik.values.email);
  };

  const apiError = createInternalAdminData?.error || addInternalAdminRoleData?.error;
  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;

  useAssignUser(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    addingUserStatusData?.body?.is_available_to_add,
    apiError
  );

  const addingUserStatus = addingUserStatusData?.body?.code;
  const isDisabledForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const formError = getExcludedResponseError(apiError, TRIAL_MODE_SUBSCRIPTION_REQUIRED_ERROR_MESSAGE);
  const isLoading = isPendingCreateInternalAdmin || isPendingAddInternalAdminRole;
  const isNotAvailableToAddUser = addingUserStatusData?.body && !addingUserStatusData?.body?.is_available_to_add;
  const isDisabledSave = isNotAvailableToAddUser || !submittedEmailRequest || hasApiError;

  return (
    <>
      <FormikForm value={formik} error={formError}>
        <FormikPanelRadioGroup
          name='role'
          label='Role*'
          options={SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS}
          apiError={apiError}
        />
        <SearchAdminEmailInput
          email={formik.values.email}
          onSearch={searchUserEmail}
          onSubmit={submitUserEmail}
          assignmentStatus={addingUserStatus}
          error={formik.errors?.email}
          apiError={apiError}
        />
        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          placeholder='Enter Mobile Phone Number With Country Code'
          apiError={apiError}
          disabled={isDisabledForm}
        />

        <FormikInput
          name='first_name'
          label='First Name*'
          placeholder='Enter First Name'
          apiError={apiError}
          disabled={isDisabledForm}
        />
        <FormikInput
          name='middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          apiError={apiError}
          disabled={isDisabledForm}
        />
        <FormikInput
          name='last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          apiError={apiError}
          disabled={isDisabledForm}
        />

        <ButtonGroup marginTop='28px'>
          <Button variant='secondary' onClick={onCancel}>
            Cancel
          </Button>
          <Button type='submit' isLoading={isLoading} disabled={isDisabledSave}>
            Save
          </Button>
        </ButtonGroup>
      </FormikForm>
      <SubscriptionRequiredModal
        isOpen={hasSubscriptionRequiredError}
        onClose={resetSubscriptionRequiredError}
        onConfirm={() => navigate(getAcceptMasterServiceAgreementPath())}
      >
        <li>Add additional team members and administrators now</li>
        <li>Send your wire requests</li>
        <li>Share your wire instructions with depositors</li>
        <li>Access advanced ID verification tools</li>
      </SubscriptionRequiredModal>
    </>
  );
};
